"use client"

import { useState, useEffect, useRef } from "react"
import styles from "./StrikethroughText.module.css"
import clsx from "clsx"

const STRIKE_DURATION_MS = 350
const STAGGER_PER_LINE_MS = 60

interface LineRect {
  top: number
  left: number
  width: number
  height: number
}

export interface StrikethroughTextProps {
  /** Text content (can include newlines). */
  text: string
  /** When true, run strike-through animation and gray text; when false, show normal text. */
  struck: boolean
  /** Called when the strike-through animation has finished (all lines drawn). */
  onStrikeComplete?: () => void
  /** Optional class for the root container. */
  className?: string
  /** Optional class for the text element (e.g. bodyText, callout). */
  textClassName?: string
}

export function StrikethroughText({
  text,
  struck,
  onStrikeComplete,
  className,
  textClassName = "bodyText",
}: StrikethroughTextProps) {
  const [lineRects, setLineRects] = useState<LineRect[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!struck) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      setLineRects([])
      return
    }
  }, [struck])

  useEffect(() => {
    if (!struck || !textRef.current || !containerRef.current) return

    const measure = () => {
      const range = document.createRange()
      range.selectNodeContents(textRef.current!)
      const rects = range.getClientRects()
      const containerRect = containerRef.current!.getBoundingClientRect()
      const rectList: LineRect[] = []
      for (let i = 0; i < rects.length; i++) {
        const r = rects[i]
        if (r.width > 0 && r.height > 0) {
          rectList.push({
            left: r.left - containerRect.left,
            top: r.top - containerRect.top,
            width: r.width,
            height: r.height,
          })
        }
      }
      range.detach()
      window.getSelection()?.removeAllRanges()
      setLineRects(rectList)

      const totalStrikeMs =
        rectList.length * STAGGER_PER_LINE_MS + STRIKE_DURATION_MS
      timeoutRef.current = setTimeout(() => {
        onStrikeComplete?.()
      }, totalStrikeMs)
    }

    const raf = requestAnimationFrame(measure)
    return () => {
      cancelAnimationFrame(raf)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [struck, onStrikeComplete])

  return (
    <div ref={containerRef} className={clsx(styles.container, className)}>
      <div
        ref={textRef}
        className={clsx(textClassName, styles.text, struck && styles.struck)}
      >
        {text}
      </div>
      {struck &&
        lineRects.map((rect, i) => (
          <div
            key={i}
            className={styles.strikeLine}
            style={{
              left: rect.left,
              top: rect.top + rect.height / 2 - 1,
              width: 0,
              height: 2,
              ["--strike-width" as string]: `${rect.width}px`,
              animationDelay: `${i * STAGGER_PER_LINE_MS}ms`,
            }}
            aria-hidden
          />
        ))}
    </div>
  )
}
